import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Box,
  Stack,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material'
import { Security as SecurityIcon, Key as KeyIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { authService, MfaVerifyResponse } from '../../services/authService'

interface MfaVerificationDialogProps {
  open: boolean
  mfaSessionToken: string
  onClose: () => void
  onSuccess: (response: MfaVerifyResponse) => void
}

export const MfaVerificationDialog: React.FC<MfaVerificationDialogProps> = ({
  open,
  mfaSessionToken,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation('auth')
  const [verificationCode, setVerificationCode] = useState('')
  const [backupCode, setBackupCode] = useState('')
  const [useBackupCode, setUseBackupCode] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [timeRemaining, setTimeRemaining] = useState(300) // 5分 = 300秒

  // タイマー
  useEffect(() => {
    if (!open) return

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timer)
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [open])

  // ダイアログを開いたらリセット
  useEffect(() => {
    if (open) {
      setVerificationCode('')
      setBackupCode('')
      setUseBackupCode(false)
      setError(null)
      setTimeRemaining(300)
    }
  }, [open])

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const handleVerify = async () => {
    setError(null)

    if (useBackupCode) {
      if (!backupCode || backupCode.length < 8) {
        setError('バックアップコードを入力してください')
        return
      }
    } else {
      if (!verificationCode || verificationCode.length !== 6) {
        setError('6桁の認証コードを入力してください')
        return
      }
    }

    try {
      setLoading(true)
      let response: MfaVerifyResponse

      if (useBackupCode) {
        response = await authService.verifyMfaBackup(mfaSessionToken, backupCode)
      } else {
        response = await authService.verifyMfa(mfaSessionToken, verificationCode)
      }

      onSuccess(response)
    } catch (err: any) {
      setError(err.response?.data?.error || '認証に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 6)
    setVerificationCode(value)
  }

  const handleBackupCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // ハイフンを許可
    const value = e.target.value
      .replace(/[^0-9A-Za-z-]/g, '')
      .toUpperCase()
      .slice(0, 9)
    setBackupCode(value)
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading) {
      handleVerify()
    }
  }

  if (timeRemaining === 0) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SecurityIcon color="error" />
          {t('mfa.expired', 'セッション期限切れ')}
        </DialogTitle>
        <DialogContent>
          <Alert severity="error">
            {t(
              'mfa.expiredMessage',
              'MFAセッションが期限切れになりました。再度ログインしてください。'
            )}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="contained">
            {t('mfa.backToLogin', 'ログイン画面に戻る')}
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SecurityIcon color="primary" />
        {t('mfa.title', '2段階認証')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {t('mfa.timeRemaining', '残り時間')}: {formatTime(timeRemaining)}
          </Typography>

          {error && <Alert severity="error">{error}</Alert>}

          {useBackupCode ? (
            <>
              <Typography variant="body2" color="text.secondary">
                {t('mfa.enterBackupCode', 'バックアップコードを入力してください（例: ABCD-1234）')}
              </Typography>

              <TextField
                label={t('mfa.backupCodeLabel', 'バックアップコード')}
                value={backupCode}
                onChange={handleBackupCodeChange}
                onKeyPress={handleKeyPress}
                placeholder="ABCD-1234"
                inputProps={{
                  maxLength: 9,
                  style: {
                    textAlign: 'center',
                    fontSize: '1.2rem',
                    letterSpacing: '0.2rem',
                    fontFamily: 'monospace',
                  },
                }}
                fullWidth
                autoFocus
              />

              <Link
                component="button"
                variant="body2"
                onClick={() => {
                  setUseBackupCode(false)
                  setBackupCode('')
                  setError(null)
                }}
                sx={{ textAlign: 'center' }}
              >
                <KeyIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                {t('mfa.useAuthenticator', '認証アプリを使用')}
              </Link>
            </>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary">
                {t('mfa.enterCode', '認証アプリに表示されている6桁のコードを入力してください。')}
              </Typography>

              <TextField
                label={t('mfa.codeLabel', '認証コード')}
                value={verificationCode}
                onChange={handleCodeChange}
                onKeyPress={handleKeyPress}
                placeholder="000000"
                inputProps={{
                  maxLength: 6,
                  style: {
                    textAlign: 'center',
                    fontSize: '1.5rem',
                    letterSpacing: '0.5rem',
                    fontFamily: 'monospace',
                  },
                }}
                fullWidth
                autoFocus
              />

              <Link
                component="button"
                variant="body2"
                onClick={() => {
                  setUseBackupCode(true)
                  setVerificationCode('')
                  setError(null)
                }}
                sx={{ textAlign: 'center' }}
              >
                <KeyIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                {t('mfa.useBackupCode', 'バックアップコードを使用')}
              </Link>
            </>
          )}

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="outlined" onClick={onClose} disabled={loading} fullWidth>
              {t('common.cancel', 'キャンセル')}
            </Button>
            <Button
              variant="contained"
              onClick={handleVerify}
              disabled={
                loading || (useBackupCode ? backupCode.length < 8 : verificationCode.length !== 6)
              }
              fullWidth
            >
              {loading ? <CircularProgress size={24} /> : t('mfa.verify', '認証')}
            </Button>
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  )
}
